import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '@kss/api';
import { Alert, Button, Card, useToast, usePincodeLookup } from '@kss/ui';
import { ArrowLeft, ImagePlus } from 'lucide-react';
import { AuthImg } from '../../components/AuthImg';

/**
 * Unified Party master — one rich form for a Customer, Company, Supplier or
 * Vendor (replaces the bare Customer master). Captures GSTIN/PAN, business
 * registration details (org/industry type, licence, FSSAI, CIN, website),
 * full address, a logo image and a bank-account block.
 *
 * Backend reality: only Customer (/crm/Customer) and Supplier
 * (/purchase/Supplier) exist, and neither has columns for the bank block or
 * the business-registration fields (gap G87). So the party type, those
 * fields, the logo URL and the bank block are all stored in the record's
 * `description` JSON. Customer/Company → Customer entity; Supplier/Vendor →
 * Supplier entity.
 *
 * Edit mode: /panel/parties/new?type=customer|supplier&id=<id>
 */

type PartyType = 'Customer' | 'Company' | 'Supplier' | 'Vendor';
type TaxMode = 'GSTIN' | 'PAN' | 'None';
const PARTY_TYPES: PartyType[] = ['Customer', 'Company', 'Supplier', 'Vendor'];
const ORG_TYPES = ['Individual', 'Proprietorship', 'Partnership', 'Private Limited', 'Public Limited', 'LLP', 'HUF', 'Trust', 'Society', 'Other'];
const isSupplierType = (t: PartyType) => t === 'Supplier' || t === 'Vendor';

interface BankBlock { accountHolderName: string; bankName: string; accountNumber: string; ifscCode: string; branch: string }
interface BizBlock { franchiseName: string; organisationType: string; industryType: string; licenseNumber: string; fssaiNumber: string; website: string; cin: string }
const emptyBank = (): BankBlock => ({ accountHolderName: '', bankName: '', accountNumber: '', ifscCode: '', branch: '' });
const emptyBiz = (): BizBlock => ({ franchiseName: '', organisationType: '', industryType: '', licenseNumber: '', fssaiNumber: '', website: '', cin: '' });

const pickList = (d: unknown): Array<Record<string, unknown>> => {
  if (Array.isArray(d)) return d as Array<Record<string, unknown>>;
  const o = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } } | null;
  if (Array.isArray(o?.items)) return o!.items as Array<Record<string, unknown>>;
  if (Array.isArray(o?.data)) return o!.data as Array<Record<string, unknown>>;
  if (o?.data && Array.isArray((o.data as { items?: unknown[] }).items)) return (o.data as { items: unknown[] }).items as Array<Record<string, unknown>>;
  return [];
};
const rid = (p: string) => `${p}-${Date.now().toString(36).toUpperCase().slice(-6)}`;

export default function PartyForm() {
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const editType = params.get('type'); // 'customer' | 'supplier' | null
  const editId = params.get('id');
  const isEdit = !!editType && !!editId;
  const fileRef = useRef<HTMLInputElement>(null);

  const [partyType, setPartyType] = useState<PartyType>(editType === 'supplier' ? 'Supplier' : 'Customer');
  const [taxMode, setTaxMode] = useState<TaxMode>('None');
  const [taxNumber, setTaxNumber] = useState('');
  const [code, setCode] = useState(isEdit ? '' : rid('PTY'));
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [biz, setBiz] = useState<BizBlock>(emptyBiz());
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [pincode, setPincode] = useState('');
  const [country, setCountry] = useState('India');
  const [bank, setBank] = useState<BankBlock>(emptyBank());
  const [imageUrl, setImageUrl] = useState('');
  const [openingBalance, setOpeningBalance] = useState(0);
  const [creditLimit, setCreditLimit] = useState(0);
  const [paymentTermDays, setPaymentTermDays] = useState(0);
  const [editRowId, setEditRowId] = useState<number | string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Area PIN → City / State auto-fill (the same hook the Customer / Company
  // master forms use). Only fills blanks so it never clobbers a manual edit.
  const pinLookup = usePincodeLookup(pincode);
  useEffect(() => {
    if (pinLookup.status !== 'ok') return;
    if (pinLookup.city) setCity((c) => c || pinLookup.city || '');
    if (pinLookup.state) setState((s) => s || pinLookup.state || '');
    if (pinLookup.country) setCountry((c) => c || pinLookup.country || 'India');
  }, [pinLookup.status, pinLookup.city, pinLookup.state, pinLookup.country]);

  // ── Edit hydrate ──
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    void (async () => {
      try {
        const basePath = editType === 'supplier' ? '/purchase/Supplier' : '/crm/Customer';
        const rows = pickList((await apiClient.get(basePath)).data);
        const row = rows.find((x) => String(x.id) === String(editId) || String(x.code) === String(editId));
        if (!row || cancelled) return;
        setEditRowId((row.id ?? null) as number | string | null);
        setCode(String(row.code ?? rid('PTY')));
        setName(String(row.name ?? ''));
        setPhone(String(row.phone ?? ''));
        setEmail(String(row.email ?? ''));
        setCity(String(row.city ?? ''));
        setState(String(row.state ?? ''));
        setPincode(String(row.pinCode ?? ''));
        setCountry(String(row.country ?? 'India'));
        setAddress(String(row.billingAddress ?? row.address ?? ''));
        setOpeningBalance(Number(row.outstandingBalance ?? 0));
        setCreditLimit(Number(row.creditLimit ?? 0));
        setPaymentTermDays(Number(row.paymentTermDays ?? 0));
        const gstin = String(row.gstin ?? ''); const pan = String(row.pan ?? '');
        if (gstin) { setTaxMode('GSTIN'); setTaxNumber(gstin); }
        else if (pan) { setTaxMode('PAN'); setTaxNumber(pan); }
        try {
          const env = JSON.parse(String(row.description ?? '{}')) as { bank?: BankBlock; biz?: BizBlock; imageUrl?: string; partyType?: PartyType };
          if (env.bank) setBank({ ...emptyBank(), ...env.bank });
          if (env.biz) setBiz({ ...emptyBiz(), ...env.biz });
          if (env.imageUrl) setImageUrl(env.imageUrl);
          if (env.partyType && PARTY_TYPES.includes(env.partyType)) setPartyType(env.partyType);
          else setPartyType(editType === 'supplier' ? 'Supplier' : 'Customer');
        } catch { /* no envelope */ }
      } catch { /* blank */ }
    })();
    return () => { cancelled = true; };
  }, [isEdit, editType, editId]);

  const taxLabel = taxMode === 'GSTIN' ? 'GSTIN' : taxMode === 'PAN' ? 'PAN' : '';
  const description = useMemo(() => JSON.stringify({ partyType, taxMode, bank, biz, imageUrl }), [partyType, taxMode, bank, biz, imageUrl]);

  async function onPickImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { persistImage } = await import('../../utils/persistImage');
        const url = await persistImage(String(reader.result));
        if (!url) { toast.error('Upload failed', 'Could not host the image.'); return; }
        setImageUrl(url);
        toast.success('Uploaded', 'Logo image saved');
      } catch { toast.error('Upload failed', 'Could not host the image.'); }
      finally { setUploading(false); }
    };
    reader.readAsDataURL(file);
  }

  function commonTax() {
    return { gstin: taxMode === 'GSTIN' ? taxNumber : '', pan: taxMode === 'PAN' ? taxNumber : '' };
  }
  function customerBody() {
    return {
      code, name, status: 0, description, ...commonTax(),
      customerType: partyType, phone, email,
      billingAddress: address, shippingAddress: address,
      city, state, pinCode: pincode, country, currency: 'INR',
      creditLimit, outstandingBalance: openingBalance, loyaltyPoints: 0,
    };
  }
  function supplierBody() {
    return {
      code, name, status: 0, description, ...commonTax(),
      supplierType: partyType, phone, email,
      address, city, state, pinCode: pincode, country, currency: 'INR',
      paymentTermDays, outstandingBalance: openingBalance, rating: 0,
    };
  }

  async function save() {
    if (!name.trim()) { setErr('Party name is required.'); return; }
    setSaving(true); setErr(null);
    try {
      const toSupplier = isEdit ? editType === 'supplier' : isSupplierType(partyType);
      const basePath = toSupplier ? '/purchase/Supplier' : '/crm/Customer';
      const body = toSupplier ? supplierBody() : customerBody();
      if (isEdit) {
        await apiClient.put(basePath, { ...body, ...(editRowId != null ? { id: Number(editRowId) } : {}), code });
      } else {
        await apiClient.post(basePath, body);
      }
      toast.success('Saved', `${partyType} “${name}” ${isEdit ? 'updated' : 'created'}`);
      navigate('/panel/parties');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setErr(msg); toast.error('Save failed', msg);
    } finally { setSaving(false); }
  }

  const inputCls = 'h-9 w-full rounded-md border border-border bg-surface-card px-3 text-sm outline-none focus:border-primary';
  return (
    <div className="-m-6 space-y-4 p-4">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate('/panel/parties')} className="grid h-8 w-8 place-items-center rounded border border-border hover:border-primary" aria-label="Back"><ArrowLeft size={16} /></button>
        <h1 className="text-base font-semibold">{isEdit ? 'Edit' : 'New'} Party</h1>
      </div>
      {err && <Alert variant="danger">{err}</Alert>}

      <Card className="space-y-4 p-4">
        {/* Logo + type + tax-id mode */}
        <div className="flex flex-wrap items-start gap-4">
          <button type="button" onClick={() => fileRef.current?.click()} className="grid h-24 w-24 shrink-0 place-items-center overflow-hidden rounded-full border border-dashed border-border bg-surface-app text-content-muted hover:border-primary">
            {imageUrl ? <AuthImg src={imageUrl} className="h-full w-full object-cover" /> : <span className="flex flex-col items-center gap-1 text-[10px]"><ImagePlus size={20} />{uploading ? 'Uploading…' : 'Upload'}</span>}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickImage} />
          <div className="flex flex-1 flex-col gap-3">
            <Field label="Party Type">
              <select className={inputCls} value={partyType} onChange={(e) => setPartyType(e.target.value as PartyType)} disabled={isEdit}>
                {PARTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <div className="flex items-center gap-4">
              {(['GSTIN', 'PAN', 'None'] as const).map((m) => (
                <label key={m} className="inline-flex cursor-pointer items-center gap-1.5 text-sm">
                  <input type="radio" name="taxMode" checked={taxMode === m} onChange={() => { setTaxMode(m); if (m === 'None') setTaxNumber(''); }} className="accent-primary" />
                  {m}
                </label>
              ))}
              {taxMode !== 'None' && (
                <input className={`${inputCls} max-w-xs`} value={taxNumber} onChange={(e) => setTaxNumber(e.target.value.toUpperCase())} placeholder={taxMode === 'GSTIN' ? '27AAAPL1234C1Z5' : 'AAAPL1234C'} aria-label={taxLabel} />
              )}
            </div>
          </div>
        </div>

        {/* Basic */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Party / Business Name *"><input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label="Franchise Name"><input className={inputCls} value={biz.franchiseName} onChange={(e) => setBiz({ ...biz, franchiseName: e.target.value })} /></Field>
          <Field label="Phone"><input className={inputCls} value={phone} inputMode="tel" onChange={(e) => setPhone(e.target.value.replace(/[^\d+]/g, ''))} /></Field>
          <Field label="Email"><input className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          <Field label="Website"><input className={inputCls} value={biz.website} onChange={(e) => setBiz({ ...biz, website: e.target.value })} /></Field>
        </div>

        {/* Business registration */}
        <div>
          <div className="mb-2 text-xs font-medium uppercase tracking-wider text-content-muted">Business Details</div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Organisation Type">
              <select className={inputCls} value={biz.organisationType} onChange={(e) => setBiz({ ...biz, organisationType: e.target.value })}>
                <option value="">Select…</option>
                {ORG_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Industry Type"><input className={inputCls} value={biz.industryType} onChange={(e) => setBiz({ ...biz, industryType: e.target.value })} placeholder="Restaurant, Retail, …" /></Field>
            <Field label="License Number"><input className={inputCls} value={biz.licenseNumber} onChange={(e) => setBiz({ ...biz, licenseNumber: e.target.value })} /></Field>
            <Field label="FSSAI Number"><input className={inputCls} value={biz.fssaiNumber} onChange={(e) => setBiz({ ...biz, fssaiNumber: e.target.value })} /></Field>
            <Field label="CIN"><input className={inputCls} value={biz.cin} onChange={(e) => setBiz({ ...biz, cin: e.target.value.toUpperCase() })} /></Field>
          </div>
        </div>

        {/* Address */}
        <div>
          <div className="mb-2 text-xs font-medium uppercase tracking-wider text-content-muted">Address</div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div className="sm:col-span-2"><Field label="Address"><input className={inputCls} value={address} onChange={(e) => setAddress(e.target.value)} /></Field></div>
            <Field label="City"><input className={inputCls} value={city} onChange={(e) => setCity(e.target.value)} /></Field>
            <Field label="State"><input className={inputCls} value={state} onChange={(e) => setState(e.target.value)} /></Field>
            <Field label="Area Pin"><input className={inputCls} value={pincode} inputMode="numeric" onChange={(e) => setPincode(e.target.value.replace(/[^\d]/g, ''))} /></Field>
            <Field label="Country"><input className={inputCls} value={country} onChange={(e) => setCountry(e.target.value)} /></Field>
          </div>
        </div>

        {/* Bank account */}
        <div>
          <div className="mb-2 text-xs font-medium uppercase tracking-wider text-content-muted">Bank Account</div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Account Holder"><input className={inputCls} value={bank.accountHolderName} onChange={(e) => setBank({ ...bank, accountHolderName: e.target.value })} /></Field>
            <Field label="Bank Name"><input className={inputCls} value={bank.bankName} onChange={(e) => setBank({ ...bank, bankName: e.target.value })} /></Field>
            <Field label="Account No."><input className={inputCls} value={bank.accountNumber} onChange={(e) => setBank({ ...bank, accountNumber: e.target.value })} /></Field>
            <Field label="IFSC Code"><input className={inputCls} value={bank.ifscCode} onChange={(e) => setBank({ ...bank, ifscCode: e.target.value.toUpperCase() })} /></Field>
            <Field label="Branch"><input className={inputCls} value={bank.branch} onChange={(e) => setBank({ ...bank, branch: e.target.value })} /></Field>
          </div>
        </div>

        {/* Balances */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Opening Balance"><input type="number" className={inputCls} value={openingBalance || ''} onChange={(e) => setOpeningBalance(Number(e.target.value) || 0)} /></Field>
          {!isSupplierType(partyType) && (
            <Field label="Credit Limit"><input type="number" className={inputCls} value={creditLimit || ''} onChange={(e) => setCreditLimit(Number(e.target.value) || 0)} /></Field>
          )}
          {isSupplierType(partyType) && (
            <Field label="Payment Term (days)"><input type="number" className={inputCls} value={paymentTermDays || ''} onChange={(e) => setPaymentTermDays(Number(e.target.value) || 0)} /></Field>
          )}
        </div>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/panel/parties')} disabled={saving}>Cancel</Button>
        <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Party'}</Button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="flex flex-col gap-1"><span className="text-[11px] font-medium text-content-secondary">{label}</span>{children}</label>;
}
